"use client";

/**
 * Kuttos Fashion — marketplace landing ("The Fashion Journal").
 *
 * Editorial magazine layout on warm ivory: oversized Fraunces headline with an
 * image stack of live new arrivals, a scrolling ticker, a numbered category
 * index, the new-arrivals grid, brand "covers", and an espresso manifesto.
 *
 * Data (all subscription-gated server-side — locked brands never appear):
 *   getHeroSlides("homepage")  — optional CMS override for the hero copy/image
 *   getNewArrivals             — product grid + hero imagery
 *   getCategoryCounts          — category index counts
 *   getMarketplaceBrands       — brand covers + stats
 *   getFeaturedBrands          — ticker names
 */

import Link from "next/link";
import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { ArrowRight, ArrowUpRight } from "lucide-react";

// ── helpers ─────────────────────────────────────────────────────────────────

const ugx = (n: number) => `UGX ${Math.round(n).toLocaleString()}`;

type Arrival = {
  _id: string;
  name: string;
  slug: string;
  price: number;
  images: string[];
  brandName: string;
  brandSlug: string;
};

const CATEGORY_INDEX: { label: string; slug: string; countKey: string }[] = [
  { label: "Dresses", slug: "dresses", countKey: "dresses" },
  { label: "Tops & Shirts", slug: "tops-blouses", countKey: "tops" },
  { label: "Outerwear", slug: "outerwear", countKey: "outerwear" },
  { label: "Tailoring", slug: "tailoring", countKey: "tailoring" },
  { label: "Essentials", slug: "essentials", countKey: "essentials" },
  { label: "Accessories", slug: "accessories", countKey: "accessories" },
];

// ── product card ────────────────────────────────────────────────────────────

function ProductCard({ p, stamp }: { p: Arrival; stamp?: boolean }) {
  const main = p.images?.[0];
  const alt = p.images?.[1];
  return (
    <Link href={`/product/${p.slug}`} className="mk-card group">
      <div className="mk-card__media">
        {stamp && <span className="mk-card__stamp mk-stamp">New in</span>}
        {main ? (
          <>
            <img src={main} alt={p.name} loading="lazy" className={`mk-card__main ${alt ? "mk-has-alt" : ""}`} />
            {alt && <img src={alt} alt="" loading="lazy" aria-hidden className="mk-card__alt" />}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="mk-eyebrow">{p.brandName}</span>
          </div>
        )}
      </div>
      <p className="mk-card__brand">{p.brandName}</p>
      <h3 className="mk-card__name">{p.name}</h3>
      <p className="mk-card__price">{ugx(p.price)}</p>
    </Link>
  );
}

// ── page ────────────────────────────────────────────────────────────────────

export function FashionMarketplaceClient() {
  const heroSlides = useQuery(api.marketing.heroSections.getHeroSlides, { pageSlug: "homepage" });
  const newArrivals = useQuery(api.marketing.fashion.getNewArrivals, { limit: 12 }) as Arrival[] | undefined;
  const categoryCounts = useQuery(api.marketing.fashion.getCategoryCounts, {});
  const marketplaceBrands = useQuery(api.marketing.fashion.getMarketplaceBrands, {});
  const featuredBrands = useQuery(api.marketing.fashion.getFeaturedBrands, {});

  const slide = (heroSlides as any[])?.[0];
  const heroHeading: string | undefined = slide?.heading;
  const heroSub: string | undefined = slide?.subheading;
  const heroImage: string | undefined = slide?.backgroundImageUrl;

  const brands = (marketplaceBrands as any)?.brands as any[] | undefined;

  const heroImages = useMemo(() => {
    const imgs: { src: string; label: string }[] = [];
    if (heroImage) imgs.push({ src: heroImage, label: "The edit" });
    for (const p of newArrivals || []) {
      if (imgs.length >= 3) break;
      if (p.images?.[0]) imgs.push({ src: p.images[0], label: p.brandName });
    }
    return imgs.slice(0, 3);
  }, [heroImage, newArrivals]);

  const stats = useMemo(() => {
    if (!brands) return null;
    const houses = brands.length;
    const pieces = brands.reduce((s, b) => s + (b.totalProducts || 0), 0);
    return { houses, pieces };
  }, [brands]);

  const tickerNames = useMemo(() => {
    const names = (featuredBrands as any[] | undefined)?.map((b) => b.name) || [];
    const fill = ["New arrivals weekly", "Verified independent brands", "Mobile Money · Card · Pay on delivery", "Kampala & beyond"];
    const items = [...fill.slice(0, 1), ...names, ...fill.slice(1)];
    return items.length > 2 ? items : [...items, "Kuttos Fashion", "Est. Kampala"];
  }, [featuredBrands]);

  const covers = useMemo(
    () => (brands || []).filter((b) => b.coverImages?.[0] || b.profileImage).slice(0, 6),
    [brands]
  );

  const arrivals = (newArrivals || []).slice(0, 8);

  return (
    <div style={{ background: "var(--mk-paper)" }}>
      {/* ══ HERO ══════════════════════════════════════════════════════════ */}
      <section className="mk-grain">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 pt-10 md:pt-16 pb-12 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
            {/* Copy */}
            <div className="lg:col-span-7">
              <p className="mk-eyebrow mk-rise">Est. Kampala — the marketplace</p>
              <h1
                className="mk-display mk-rise mk-d1 mt-5"
                style={{ fontSize: "clamp(44px, 7.2vw, 104px)" }}
              >
                {heroHeading ? (
                  heroHeading
                ) : (
                  <>
                    Africa's finest
                    <br />
                    fashion, <em>one door</em>
                    <br />
                    down.
                  </>
                )}
              </h1>
              <p
                className="mk-rise mk-d2 mt-6 max-w-lg text-[15px] leading-relaxed"
                style={{ color: "var(--mk-ink-soft)" }}
              >
                {heroSub ||
                  "Independent houses, tailors and ateliers — verified, stocked and delivered. Shop the continent's designers without leaving your street."}
              </p>
              <div className="mk-rise mk-d3 mt-9 flex flex-wrap items-center gap-4">
                <Link href="/shop" className="mk-btn mk-btn--solid">
                  Shop the edit <ArrowRight className="h-4 w-4" />
                </Link>
                <Link href="/brands" className="mk-btn">
                  Explore the houses
                </Link>
              </div>
              {stats && (
                <div className="mk-rise mk-d4 mt-12 flex items-center gap-10">
                  <div>
                    <p className="mk-display" style={{ fontSize: 34 }}>{stats.houses}</p>
                    <p className="mk-eyebrow mt-1" style={{ fontSize: 10 }}>Fashion houses</p>
                  </div>
                  <div className="h-10 w-px" style={{ background: "var(--mk-line)" }} />
                  <div>
                    <p className="mk-display" style={{ fontSize: 34 }}>{stats.pieces.toLocaleString()}+</p>
                    <p className="mk-eyebrow mt-1" style={{ fontSize: 10 }}>Pieces in store</p>
                  </div>
                  <div className="h-10 w-px hidden sm:block" style={{ background: "var(--mk-line)" }} />
                  <div className="hidden sm:block">
                    <p className="mk-display" style={{ fontSize: 34 }}>100%</p>
                    <p className="mk-eyebrow mt-1" style={{ fontSize: 10 }}>Verified brands</p>
                  </div>
                </div>
              )}
            </div>

            {/* Image stack */}
            <div className="lg:col-span-5 relative h-[420px] md:h-[520px] hidden sm:block">
              {heroImages.length === 0 ? (
                <div className="mk-skeleton absolute inset-6" />
              ) : (
                heroImages.map((img, i) => {
                  const poses = [
                    { top: "4%", right: "6%", width: "68%", rotate: "-2.4deg", z: 3 },
                    { top: "30%", right: "38%", width: "52%", rotate: "3deg", z: 2 },
                    { top: "48%", right: "0%", width: "48%", rotate: "-5deg", z: 1 },
                  ];
                  const pos = poses[i];
                  return (
                    <figure
                      key={i}
                      className={`mk-rise mk-d${i + 2} absolute`}
                      style={{
                        top: pos.top,
                        right: pos.right,
                        width: pos.width,
                        transform: `rotate(${pos.rotate})`,
                        zIndex: pos.z,
                        background: "#fff",
                        padding: 10,
                        paddingBottom: 34,
                        border: "1px solid var(--mk-line)",
                        boxShadow: "0 24px 48px -24px rgba(27,20,16,0.35)",
                      }}
                    >
                      <div style={{ aspectRatio: "3/4", overflow: "hidden", background: "var(--mk-paper-deep)" }}>
                        <img src={img.src} alt={img.label} className="w-full h-full object-cover" />
                      </div>
                      <figcaption
                        className="absolute bottom-2 left-3 text-[9px] font-bold uppercase tracking-[0.24em]"
                        style={{ color: "var(--mk-ink-faint)" }}
                      >
                        {img.label}
                      </figcaption>
                    </figure>
                  );
                })
              )}
              {/* Clay medallion */}
              <div
                className="mk-rise mk-d5 absolute -bottom-2 left-2 h-24 w-24 rounded-full flex items-center justify-center text-center"
                style={{ background: "var(--mk-clay)", transform: "rotate(-12deg)", zIndex: 4 }}
              >
                <span className="text-[9px] font-bold uppercase tracking-[0.2em] leading-[1.5] text-white">
                  New in<br />every<br />week
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TICKER ════════════════════════════════════════════════════════ */}
      <div className="mk-ticker" aria-hidden>
        <div className="mk-ticker__track">
          {[0, 1].map((dup) => (
            <span key={dup} className="inline-flex">
              {tickerNames.map((n, i) => (
                <span key={`${dup}-${i}`} className="mk-ticker__item">
                  {n} <span className="mk-ticker__dot">✦</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* ══ CATEGORY INDEX ════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-[1440px] px-4 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="mk-eyebrow">The index</p>
            <h2 className="mk-display mt-4" style={{ fontSize: "clamp(32px, 3.6vw, 52px)" }}>
              Shop by<br /><em>department</em>
            </h2>
            <p className="mt-5 max-w-xs text-sm leading-relaxed" style={{ color: "var(--mk-ink-soft)" }}>
              Six departments, hundreds of pieces — cut, stitched and finished by
              independent African houses.
            </p>
            <Link href="/shop" className="mk-link mt-8">
              View everything <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="lg:col-span-8">
            <hr className="mk-rule-strong" />
            {CATEGORY_INDEX.map((c, i) => {
              const count = (categoryCounts as any)?.[c.countKey] ?? null;
              return (
                <Link key={c.slug} href={`/shop?category=${c.slug}`} className="mk-cat-row group">
                  <span className="flex items-baseline gap-5">
                    <span className="mk-index">0{i + 1}</span>
                    <span className="mk-cat-row__name">{c.label}</span>
                  </span>
                  <span className="mk-cat-row__count">
                    {count === null ? "—" : `${count} piece${count === 1 ? "" : "s"}`}
                    <ArrowUpRight className="inline-block ml-3 h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ NEW ARRIVALS ══════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-[1440px] px-4 md:px-8 pb-16 md:pb-24">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <p className="mk-eyebrow">Fresh off the rail</p>
            <h2 className="mk-display mt-3" style={{ fontSize: "clamp(30px, 3.4vw, 48px)" }}>
              New <em>arrivals</em>
            </h2>
          </div>
          <Link href="/shop?sortBy=newest" className="mk-link shrink-0 mb-2">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {newArrivals === undefined ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i}>
                <div className="mk-skeleton" style={{ aspectRatio: "3/4" }} />
                <div className="mk-skeleton mt-4 h-3 w-1/3" />
                <div className="mk-skeleton mt-2 h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : arrivals.length === 0 ? (
          <div className="mk-frame p-14 text-center">
            <p className="mk-display" style={{ fontSize: 26 }}>The rail is being restocked</p>
            <p className="mt-2 text-sm" style={{ color: "var(--mk-ink-soft)" }}>
              New pieces land every week — check back shortly.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 gap-y-10">
            {arrivals.map((p, i) => (
              <ProductCard key={p._id} p={p} stamp={i < 2} />
            ))}
          </div>
        )}
      </section>

      {/* ══ THE HOUSES (brand covers) ═════════════════════════════════════ */}
      <section style={{ background: "var(--mk-paper-deep)", borderTop: "1px solid var(--mk-line)", borderBottom: "1px solid var(--mk-line)" }}>
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="mk-eyebrow">The directory</p>
              <h2 className="mk-display mt-3" style={{ fontSize: "clamp(30px, 3.4vw, 48px)" }}>
                Houses of <em>the moment</em>
              </h2>
            </div>
            <Link href="/brands" className="mk-link shrink-0 mb-2">
              All brands <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {marketplaceBrands === undefined ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="mk-skeleton" style={{ aspectRatio: "4/5" }} />
              ))}
            </div>
          ) : covers.length === 0 ? (
            <div className="mk-frame p-14 text-center" style={{ background: "var(--mk-paper)" }}>
              <p className="mk-display" style={{ fontSize: 26 }}>The houses are moving in</p>
              <p className="mt-2 text-sm" style={{ color: "var(--mk-ink-soft)" }}>
                Verified brands appear here the moment they open.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {covers.map((b, i) => (
                <Link key={b._id} href={`/brands/${b.slug}`} className="mk-cover">
                  {(b.coverImages?.[0] || b.profileImage) && (
                    <img src={b.coverImages?.[0] || b.profileImage} alt={b.name} loading="lazy" />
                  )}
                  <div className="mk-cover__scrim" />
                  <div className="mk-cover__masthead">
                    <span>Kuttos Journal</span>
                    <span>№ {String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="mk-cover__body">
                    <p className="mk-cover__name">{b.name}</p>
                    <p className="mk-cover__meta">
                      {b.totalProducts > 0 ? `${b.totalProducts} pieces` : "New house"}
                      {b.address?.city ? ` · ${b.address.city}` : ""}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ MANIFESTO ═════════════════════════════════════════════════════ */}
      <section style={{ background: "var(--mk-espresso)" }}>
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-20 md:py-28 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em]" style={{ color: "var(--mk-gold)" }}>
            The Kuttos promise
          </p>
          <h2
            className="mx-auto mt-6 max-w-4xl"
            style={{
              fontFamily: "var(--mk-serif)",
              fontSize: "clamp(30px, 4.4vw, 60px)",
              lineHeight: 1.08,
              fontWeight: 440,
              letterSpacing: "-0.015em",
              color: "#f6f2ea",
            }}
          >
            Every piece you see is <em style={{ color: "var(--mk-gold)" }}>in stock, verified</em> and
            backed by the house that made it.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed" style={{ color: "rgba(246,242,234,0.65)" }}>
            No dropshipping, no ghosts — only active, paying fashion houses can list on
            Kuttos. Pay by Mobile Money, card, or when it arrives at your door.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link href="/shop" className="mk-btn mk-btn--clay">
              Start shopping <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://app.kuttostechnologies.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mk-btn mk-btn--ghost-light"
            >
              Sell on Kuttos <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
        {/* Gold ticker */}
        <div className="mk-ticker mk-ticker--espresso" aria-hidden>
          <div className="mk-ticker__track">
            {[0, 1].map((dup) => (
              <span key={dup} className="inline-flex">
                {["Cut locally", "Worn everywhere", "Verified houses only", "Delivered to your door", "Kuttos Fashion — Est. Kampala"].map((n, i) => (
                  <span key={`${dup}-${i}`} className="mk-ticker__item">
                    {n} <span className="mk-ticker__dot">✦</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
